/**
 * Generic string helpers shared across the app.
 *
 * @module stringUtils
 * @remarks Used heavily by the Entities module (`entityDataUtils`, `formatters`) to turn
 * API keys like `contact_information` or `someFieldName` into readable labels.
 */

/**
 * Capitalizes the first real letter of a string, ignoring any leading characters that aren't letters
 * @param {string} string
 * @returns {string}
 */
export const capitalizeFirstLetterOfString = (string) => {
  if (!string) return "";

  const firstLetterIndex = string.search(/[a-zA-Z]/);
  if (firstLetterIndex === -1) return string;

  return (
    string.slice(0, firstLetterIndex) +
    string[firstLetterIndex].toUpperCase() +
    string.slice(firstLetterIndex + 1)
  );
};

/**
 * Capitalizes the first letter of each space separated word in a string
 *
 * @param {string} [str]
 * @returns {string}
 */
export const convertStringToCapitalizedWordsString = (str) => {
  if (!str) return "";
  return str
    .split(" ")
    .map((item) => capitalizeFirstLetterOfString(item))
    .join(" ");
};

/**
 * Converts a plain string to title case
 * Lowercasing everything first then capitalizing the first letter of each word ensures correct title casing
 *
 * @param {string} [str]
 * @returns {string}
 */
export const convertStringToTitleCaseString = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(/[\s-]+/)
    .map((item) => capitalizeFirstLetterOfString(item))
    .join(" ");
};

/**
 * Converts an API key (snake_case or camelCase) into a human-readable label with each word capitalized
 * e.g. "contact_information" → "Contact Information", "someFieldName" → "Some Field Name"
 *
 * @param {string} [key]
 * @returns {string}
 */
export const convertApiKeyToLabelString = (key) => {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((item) => capitalizeFirstLetterOfString(item))
    .join(" ");
};

/**
 * Converts a slug (underscore or hyphen-separated string) into a capitalized label
 * Treats both '_' and '-' as word separators without altering existing character casing
 * e.g. "bi_weekly" => "Bi Weekly", "semi-annual" => "Semi Annual"
 *
 * @param {string} [slug]
 * @returns {string}
 */
export const convertSlugToLabelString = (slug) => {
  if (!slug) return "";
  return String(slug)
    .replace(/[_-]+/g, " ")
    .split(" ")
    .map((item) => capitalizeFirstLetterOfString(item))
    .join(" ");
};

/**
 * Normalize text for human-friendly search / compare.
 * Lowercases, strips diacritics (é → e), collapses punctuation to spaces.
 *
 * @param {unknown} value
 * @returns {string}
 */
export const normalizeSearchText = (value) => {
  if (value === null || value === undefined) return "";
  const raw = String(value);
  let normalized;
  try {
    normalized = raw.normalize("NFKC").trim().toLowerCase();
  } catch {
    normalized = raw.trim().toLowerCase();
  }

  try {
    return normalized
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch {
    // Fallback for environments without Unicode property escapes support.
    return normalized
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^A-Za-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
};

/**
 * Accent/case/punctuation-insensitive "contains".
 * Empty / blank query matches everything.
 * `query` may already be pre-normalized via `normalizeSearchText`.
 *
 * @param {unknown} haystack
 * @param {unknown} query
 * @returns {boolean}
 */
export const matchesSearchText = (haystack, query) => {
  const needle = normalizeSearchText(query);
  if (!needle) return true;
  return normalizeSearchText(haystack).includes(needle);
};